<template>
  <div class="form-group kiosk-video-input">
    <label>{{$t('settingTable.kioskVideo')}}</label>
    <div v-for="(video, index) in value" :key="video.id" class="kiosk-video-item">
      <video :src="video.url" controls muted preload="metadata" class="kiosk-video-preview"></video>
      <div class="kiosk-video-info">
        <span>#{{video.id}} {{video.original_name || video.name}}</span>
        <small v-if="video.size">({{formatSize(video.size)}})</small>
        <button type="button" class="btn btn-danger btn-sm" :disabled="uploading || removing" @click="remove(index)">
          <i class="fa fa-trash" aria-hidden="true"></i> {{$t('settingTable.kioskVideoRemove')}}
        </button>
      </div>
    </div>
    <el-upload
      action=""
      :on-change="handleChange"
      :on-remove="handleRemove"
      :limit="1"
      :auto-upload="false"
      accept=".mp4,.webm,video/mp4,video/webm"
      ref="upload">
      <el-button slot="trigger" class="btn add mar" size="small" type="primary" :disabled="uploading">{{$t('upload.browse')}}</el-button>
      <el-button :disabled="!file || uploading" :loading="uploading" style="margin-left: 10px;" class="btn add mar" size="small" type="primary" @click="upload">{{$t('upload.upload')}}</el-button>
      <div class="el-upload__tip" slot="tip">{{$t(tableId ? 'settingTable.kioskVideoHintAuto' : 'settingTable.kioskVideoHint')}}</div>
    </el-upload>
    <el-progress v-if="uploading" :percentage="progress" class="pad-bt-5"></el-progress>
  </div>
</template>
<script>
  import swal from 'sweetalert2'

  const MAX_SIZE = 50 * 1024 * 1024
  // 8MB raw -> ~11MB base64 per request
  const CHUNK_SIZE = 8 * 1024 * 1024
  const CHUNK_RETRIES = 3
  const ALLOWED_EXTENSIONS = ['mp4', 'webm']

  export default {
    name: 'kiosk-video-input',
    props: {
      // [{id, name, original_name, size, url}]
      value: {
        type: Array,
        default: () => []
      },
      companyId: null,
      locationId: null,
      // existing kiosk id: upload/remove are saved on the kiosk right away (no form save)
      tableId: null,
      // single video for now: a new upload replaces the current one
      multiple: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        file: null,
        uploading: false,
        removing: false,
        progress: 0
      }
    },
    methods: {
      toast (type, text) {
        swal({
          toast: true,
          type: type,
          text: text,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000
        }).then(() => {}).catch(() => {})
      },
      formatSize (bytes) {
        return (bytes / 1024 / 1024).toFixed(1) + ' MB'
      },
      handleChange (file) {
        const extension = (file.name.split('.').pop() || '').toLowerCase()
        if (!ALLOWED_EXTENSIONS.includes(extension)) {
          this.toast('warning', this.$t('validations.wrongFormat'))
          this.$refs.upload.clearFiles()
          this.file = null
        } else if (file.size > MAX_SIZE) {
          this.toast('warning', this.$t('settingTable.kioskVideoTooBig'))
          this.$refs.upload.clearFiles()
          this.file = null
        } else {
          this.file = file.raw
        }
      },
      handleRemove (file, fileList) {
        if (fileList.length === 0) {
          this.file = null
        }
      },
      remove (index) {
        const videos = this.value.slice()
        const removed = videos.splice(index, 1)[0]
        if (!removed) {
          return
        }
        // takes it off the kiosk right away and deletes the video file
        this.removing = true
        this.apiRequest('/videos/kiosk-remove', 'POST', {
          token: this.$session.get('token'),
          table_id: this.tableId || null,
          video_id: removed.id
        }, (response) => {
          this.removing = false
          if (response && response.status) {
            this.$emit('input', videos)
            this.toast('success', this.$t('settingTable.kioskVideoRemoved'))
          } else {
            this.toast('error', response && typeof response.data === 'string' ? response.data : this.$t('general.error'))
          }
        })
      },
      postChunk (data) {
        return new Promise((resolve) => {
          this.apiRequest('/videos/upload', 'POST', data, resolve)
        })
      },
      async sendChunk (data) {
        let response = null
        for (let attempt = 0; attempt < CHUNK_RETRIES; attempt++) {
          response = await this.postChunk(data)
          if (response && response.status) {
            return response
          }
        }
        return response
      },
      async upload () {
        const file = this.file
        if (!file || this.uploading) {
          return
        }
        this.uploading = true
        this.progress = 0

        const uploadId = Date.now().toString(36) + Math.random().toString(36).slice(2, 10)
        let response = null
        try {
          for (let offset = 0; offset < file.size; offset += CHUNK_SIZE) {
            const end = Math.min(offset + CHUNK_SIZE, file.size)
            const chunk = await this.getBase64(file.slice(offset, end))
            response = await this.sendChunk({
              token: this.$session.get('token'),
              company_id: this.companyId,
              location_id: this.locationId,
              table_id: this.tableId || null,
              append: this.multiple ? 1 : 0,
              file_name: file.name,
              upload_id: uploadId,
              offset: offset,
              total_size: file.size,
              is_last: end >= file.size ? 1 : 0,
              chunk: chunk
            })
            if (!response || !response.status) {
              break
            }
            this.progress = Math.round(end / file.size * 100)
          }
        } catch (e) {
          response = null
        }
        this.uploading = false

        if (response && response.status && response.data && response.data.id) {
          const videos = this.multiple ? this.value.concat([response.data]) : [response.data]
          this.$emit('input', videos)
          this.file = null
          this.$refs.upload.clearFiles()
          this.toast('success', this.$t(this.tableId ? 'settingTable.kioskVideoSaved' : 'settingTable.kioskVideoUploaded'))
        } else {
          const message = response && typeof response.data === 'string' ? response.data : this.$t('general.error')
          this.toast('error', message)
        }
      }
    }
  }
</script>

<style scoped>
  .kiosk-video-item {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;
    flex-wrap: wrap;
  }
  .kiosk-video-preview {
    width: 220px;
    max-height: 140px;
    background: #000;
    border-radius: 4px;
  }
  .kiosk-video-info {
    display: flex;
    flex-direction: column;
    gap: 6px;
    align-items: flex-start;
  }
</style>



// WEBPACK FOOTER //
// src/components/UIComponents/Inputs/KioskVideoInput.vue